import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import ForgotPassword from "@/pages/ForgotPassword";

import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import Genres from "@/pages/Genres";
import Tropes from "@/pages/Tropes";
import Collection from "@/pages/Collection";
import Authors from "@/pages/Authors";
import AuthorDetail from "@/pages/AuthorDetail";
import BookDetail from "@/pages/BookDetail";
import SeriesDetail from "@/pages/SeriesDetail";
import Reader from "@/pages/Reader";
import Premium from "@/pages/Premium";
import Auth from "@/pages/Auth";
import Profile from "@/pages/Profile";
import Library from "@/pages/Library";
import SearchPage from "@/pages/Search";
import PaymentResult from "@/pages/PaymentResult";
import StaticPage from "@/pages/StaticPage";
import WallOfLegends from "@/pages/WallOfLegends";
import Admin from "@/pages/admin/Admin";
import Advertiser from "@/pages/Advertiser";
import "@/index.css";

function AuthCallback() {
  const nav = useNavigate();
  const { login } = useApp();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sid });
        login(data.token, data.user);
        const dest = data.user?.role === "advertiser" ? "/advertiser" : "/profile";
        window.history.replaceState(null, "", dest);
        nav(dest);
      } catch {
        nav("/login");
      }
    })();
  }, []); // eslint-disable-line
  return <div className="min-h-screen grid place-items-center text-muted-foreground">Signing you in…</div>;
}

function Shell() {
  const location = useLocation();
  const { user } = useApp();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // Advertiser accounts only see the advertiser panel — never the reader experience
  if (user?.role === "advertiser" && !location.pathname.startsWith("/advertiser")) {
    return <Navigate to="/advertiser" replace />;
  }
  const isReader = location.pathname.startsWith("/read/");
  const isAdvertiser = user?.role === "advertiser";
  return (
    <>
      <SeoHead />
      {!isReader && !isAdvertiser && <Navbar />}
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/genres" element={<Genres />} />
          <Route path="/genres/:slug" element={<Collection kind="genre" />} />
          <Route path="/tropes" element={<Tropes />} />
          <Route path="/tropes/:slug" element={<Collection kind="trope" />} />
          <Route path="/authors" element={<Authors />} />
          <Route path="/authors/:slug" element={<AuthorDetail />} />
          <Route path="/books/:slug" element={<BookDetail />} />
          <Route path="/series/:slug" element={<SeriesDetail />} />
          <Route path="/read/:slug/:number" element={<Reader />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/register" element={<Auth mode="register" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/library" element={<Library />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/wall-of-legends" element={<WallOfLegends />} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/page/:slug" element={<StaticPage />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/advertiser" element={<Advertiser />} />
          <Route path="/advertiser/login" element={<Advertiser mode="login" />} />
          <Route path="/advertiser/register" element={<Advertiser mode="register" />} />
        </Routes>
      </main>
      {!isReader && !isAdvertiser && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors theme="dark" />
        <Shell />
      </BrowserRouter>
    </AppProvider>
  );
}
